import { Fragment, useState } from "react";
import { useColorMode } from "~/providers/color-mode";
import { LoadViewerDocument, useUpdateUserNameMutation } from "~/operations";
import {
  ConfigurationPaper,
  ConfigurationList,
  ConfigurationItemText,
  ConfigurationItemSwitch,
  ConfigurationItemTextProps,
  ConfigurationItemSwitchProps,
  ConfigurationITemButtonSwitch,
} from "./configuration-items";
import { enqueueSnackbar } from "notistack";
import { useViewer } from "~/providers/viewer";

export function UserNameSection() {
  const { viewer, updateViewerSettings } = useViewer();
  const { colorBlind, colorMode, setColorMode } = useColorMode();
  const [name, setName] = useState<string>(viewer?.name || "");
  const [storeNameError, setStoreNameError] = useState<string>("");
  const [isUsernameDirty, setIsUsernameDirty] = useState(false);
  const [updateUserName] = useUpdateUserNameMutation();

  const handleChangeName: ConfigurationItemTextProps["onChange"] = (event) => {
    event.preventDefault();
    const newValue = event.target.value;
    setName(newValue);
    setIsUsernameDirty(newValue !== viewer?.name);
  };

  const onUsernameBlur = () => {
    if (isUsernameDirty) {
      saveUsername().then();
    }
  };

  const saveUsername = async () => {
    try {
      await updateUserName({
        variables: { input: { name } },
        refetchQueries: [LoadViewerDocument],
      });

      setStoreNameError("");
      setIsUsernameDirty(false);
      enqueueSnackbar("Username saved successfully!", { variant: "success" });
    } catch (error) {
      const errorMsg = "Failed to save user name, please try again!";
      enqueueSnackbar(errorMsg, { variant: "error" });
      console.error(error);
      setStoreNameError(errorMsg);
    }
  };

  const handleSetColorBlind: ConfigurationItemSwitchProps["onChange"] = (
    _event,
    value,
  ) => {
    try {
      updateViewerSettings({
        variables: { key: "colorblind", value: value.toString() },
        refetchQueries: [LoadViewerDocument],
      }).then();
    } catch (error) {
      enqueueSnackbar("Failed to save color blind setting, please try again!", {
        variant: "error",
      });
      console.error(error);
    }
  };

  const handleColorModeChange = async (_: unknown, newMode: any) => {
    try {
      await setColorMode(newMode);
    } catch (error) {
      enqueueSnackbar("Failed to save color mode setting, please try again!", {
        variant: "error",
      });
      console.error(error);
    }
  };

  return (
    <Fragment>
      <ConfigurationPaper>
        <ConfigurationList title="General">
          <ConfigurationItemText
            onBlur={onUsernameBlur}
            primary="Name"
            secondary="Your full name"
            label="Name"
            value={name}
            onChange={handleChangeName}
            error={storeNameError.length !== 0}
            helperText={storeNameError}
          />

          <ConfigurationItemText
            disabled={true}
            primary="Email"
            secondary="Email used for login and notifications"
            label="Email"
            value={viewer?.email || ""}
          />
        </ConfigurationList>
      </ConfigurationPaper>

      <ConfigurationPaper>
        <ConfigurationList title="Display">
          <ConfigurationITemButtonSwitch
            mode={colorMode}
            primary="Color mode"
            secondary="Select light, dark, or system mode"
            onChange={handleColorModeChange}
          />

          <ConfigurationItemSwitch
            primary="Colorblind Mode"
            secondary="Modifies colors for easy viewing for colorblind users."
            checked={Boolean(colorBlind === "true")}
            action={colorBlind ? "On" : "Off"}
            onChange={handleSetColorBlind}
          />
        </ConfigurationList>
      </ConfigurationPaper>
    </Fragment>
  );
}
